/**
 * Deterministic mock seeds for the CashierView (B1.7).
 *
 * Designed so the end-to-end contract is exercised in Lovable previews and
 * Storybook without requiring a backend:
 *   - realistic salon catalog (grooming packages + shop SKUs)
 *   - one open session with recent GMV
 *   - an active order mid-flow (one service line) to showcase cart UX
 *   - two parked orders (typical "beklet, sıradaki müşteri" pattern)
 *
 * All monetary values are in minor units (kuruş): ₺1 = 100.
 */

import type {
  CashierCatalogCategory,
  CashierCatalogItem,
  CashierOrder,
  CashierOrderLine,
  CashierPaymentMethod,
  CashierSession,
} from './types';

const LOYALTY_REASON = 'loyalty_10';

export const CASHIER_CATEGORIES_SEED: readonly CashierCatalogCategory[] = [
  { key: 'all', label: 'Tümü', icon: 'product', count: 18 },
  { key: 'grooming', label: 'Grooming', icon: 'grooming', count: 6 },
  { key: 'boarding', label: 'Pansiyon', icon: 'boarding', count: 2 },
  { key: 'training', label: 'Eğitim', icon: 'training', count: 2 },
  { key: 'shop', label: 'Ürün', icon: 'product', count: 6 },
  { key: 'walk', label: 'Yürüyüş', icon: 'walk', count: 2 },
];

export const CASHIER_CATALOG_SEED: readonly CashierCatalogItem[] = [
  // Grooming services
  {
    id: 'svc_grooming_full',
    kind: 'service',
    sku: null,
    categoryKey: 'grooming',
    label: 'Full Grooming Paketi',
    imageUrl: null,
    priceMinor: 45_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: true,
  },
  {
    id: 'svc_grooming_bath',
    kind: 'service',
    sku: null,
    categoryKey: 'grooming',
    label: 'Yıkama + Kurulama',
    imageUrl: null,
    priceMinor: 18_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: true,
  },
  {
    id: 'svc_grooming_flea',
    kind: 'service',
    sku: null,
    categoryKey: 'grooming',
    label: 'Pire/Kene Banyosu',
    imageUrl: null,
    priceMinor: 12_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: false,
  },
  {
    id: 'svc_grooming_nail',
    kind: 'service',
    sku: null,
    categoryKey: 'grooming',
    label: 'Tırnak Kesimi',
    imageUrl: null,
    priceMinor: 6_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: false,
  },
  {
    id: 'svc_grooming_teeth',
    kind: 'service',
    sku: null,
    categoryKey: 'grooming',
    label: 'Diş Temizliği',
    imageUrl: null,
    priceMinor: 8_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: false,
  },
  {
    id: 'svc_grooming_dematting',
    kind: 'service',
    sku: null,
    categoryKey: 'grooming',
    label: 'Düğüm Açma',
    imageUrl: null,
    priceMinor: 9_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: false,
  },
  // Boarding services
  {
    id: 'svc_boarding_night',
    kind: 'service',
    sku: null,
    categoryKey: 'boarding',
    label: 'Pansiyon · 1 Gece',
    imageUrl: null,
    priceMinor: 30_000,
    taxRate: 0.2,
    unit: 'night',
    stockOnHand: null,
    isFavorite: true,
  },
  {
    id: 'svc_boarding_day',
    kind: 'service',
    sku: null,
    categoryKey: 'boarding',
    label: 'Günlük Bakım',
    imageUrl: null,
    priceMinor: 15_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: false,
  },
  // Training services
  {
    id: 'svc_training_basic',
    kind: 'service',
    sku: null,
    categoryKey: 'training',
    label: 'Temel İtaat · 1 Saat',
    imageUrl: null,
    priceMinor: 25_000,
    taxRate: 0.2,
    unit: 'hour',
    stockOnHand: null,
    isFavorite: false,
  },
  {
    id: 'svc_training_behaviour',
    kind: 'service',
    sku: null,
    categoryKey: 'training',
    label: 'Davranış Danışmanlığı',
    imageUrl: null,
    priceMinor: 35_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: false,
  },
  // Walk services
  {
    id: 'svc_walk_30',
    kind: 'service',
    sku: null,
    categoryKey: 'walk',
    label: 'Gezdirme · 30 dk',
    imageUrl: null,
    priceMinor: 10_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: false,
  },
  {
    id: 'svc_walk_60',
    kind: 'service',
    sku: null,
    categoryKey: 'walk',
    label: 'Gezdirme · 60 dk',
    imageUrl: null,
    priceMinor: 16_000,
    taxRate: 0.2,
    unit: 'session',
    stockOnHand: null,
    isFavorite: false,
  },
  // Shop products
  {
    id: 'prd_food_dog_2kg',
    kind: 'product',
    sku: '8690000000011',
    categoryKey: 'shop',
    label: 'Köpek Maması · 2 kg',
    imageUrl: null,
    priceMinor: 18_000,
    taxRate: 0.1,
    unit: 'pcs',
    stockOnHand: 24,
    isFavorite: true,
  },
  {
    id: 'prd_food_cat_1kg',
    kind: 'product',
    sku: '8690000000028',
    categoryKey: 'shop',
    label: 'Kedi Maması · 1 kg',
    imageUrl: null,
    priceMinor: 12_000,
    taxRate: 0.1,
    unit: 'pcs',
    stockOnHand: 12,
    isFavorite: false,
  },
  {
    id: 'prd_shampoo',
    kind: 'product',
    sku: '8690000000035',
    categoryKey: 'shop',
    label: 'Pet Şampuanı · 250 ml',
    imageUrl: null,
    priceMinor: 9_500,
    taxRate: 0.2,
    unit: 'pcs',
    stockOnHand: 6,
    isFavorite: false,
  },
  {
    id: 'prd_leash',
    kind: 'product',
    sku: '8690000000042',
    categoryKey: 'shop',
    label: 'Köpek Tasması',
    imageUrl: null,
    priceMinor: 15_000,
    taxRate: 0.2,
    unit: 'pcs',
    stockOnHand: 2,
    isFavorite: false,
  },
  {
    id: 'prd_toy_ball',
    kind: 'product',
    sku: '8690000000059',
    categoryKey: 'shop',
    label: 'Oyuncak Top',
    imageUrl: null,
    priceMinor: 4_500,
    taxRate: 0.2,
    unit: 'pcs',
    stockOnHand: 0,
    isFavorite: false,
  },
  {
    id: 'prd_vitamin',
    kind: 'product',
    sku: '8690000000066',
    categoryKey: 'shop',
    label: 'Multivitamin · 100 tablet',
    imageUrl: null,
    priceMinor: 22_000,
    taxRate: 0.1,
    unit: 'pcs',
    stockOnHand: 9,
    isFavorite: false,
  },
];

export const CASHIER_AVAILABLE_TENDERS_SEED: readonly CashierPaymentMethod[] = [
  'cash',
  'card',
  'gift_card',
  'loyalty_points',
  'transfer',
  'on_account',
];

export const CASHIER_SESSION_SEED: CashierSession = {
  id: 'sess_2026_04_14_demo',
  openedByStaffId: 'staff_elif',
  openedByName: 'Elif Kaya',
  openedAt: sessionOpenedAtIso(),
  closedAt: null,
  status: 'open',
  cashFloatMinor: 50_000,
  ordersCount: 7,
  grossMinor: 284_500,
  cashCollectedMinor: 112_000,
};

function sessionOpenedAtIso(): string {
  const d = new Date();
  d.setHours(9, 15, 0, 0);
  return d.toISOString();
}

function buildActiveOrder(): CashierOrder {
  const sessionId = CASHIER_SESSION_SEED.id;
  const groomingLine: CashierOrderLine = {
    lineId: 'line_1',
    itemId: 'svc_grooming_full',
    label: 'Full Grooming Paketi',
    qty: 1,
    unitPriceMinor: 45_000,
    lineTotalMinor: 40_500, // 45_000 − 4_500 loyalty
    taxRate: 0.2,
    discount: {
      kind: 'loyalty',
      valueMinor: 4_500,
      reasonCode: LOYALTY_REASON,
    },
    note: null,
  };
  const subtotal = 45_000;
  const orderDiscount = 4_500;
  const taxableBase = subtotal - orderDiscount;
  const tax = Math.round(taxableBase * 0.2);
  return {
    id: 'ord_active',
    sessionId,
    customerId: 'cust_ayse',
    customerName: 'Ayşe Demir',
    petId: 'pet_jack',
    petName: 'Jack',
    lines: [groomingLine],
    subtotalMinor: subtotal,
    orderDiscountMinor: orderDiscount,
    taxMinor: tax,
    totalMinor: taxableBase + tax,
    tenders: [],
    status: 'open',
    parkedLabel: null,
    openedAt: new Date().toISOString(),
    closedAt: null,
  };
}

function buildParkedOrder(
  idSuffix: string,
  customerName: string,
  petName: string,
  label: string,
  lines: readonly CashierOrderLine[],
): CashierOrder {
  const subtotal = lines.reduce(
    (acc, l) => acc + l.qty * l.unitPriceMinor,
    0,
  );
  const lineDiscount = lines.reduce(
    (acc, l) => acc + (l.discount?.valueMinor ?? 0),
    0,
  );
  const taxableBase = subtotal - lineDiscount;
  const tax = Math.round(taxableBase * 0.2);
  return {
    id: `ord_parked_${idSuffix}`,
    sessionId: CASHIER_SESSION_SEED.id,
    customerId: `cust_${idSuffix}`,
    customerName,
    petId: `pet_${idSuffix}`,
    petName,
    lines,
    subtotalMinor: subtotal,
    orderDiscountMinor: lineDiscount,
    taxMinor: tax,
    totalMinor: taxableBase + tax,
    tenders: [],
    status: 'parked',
    parkedLabel: label,
    openedAt: new Date(Date.now() - 40 * 60_000).toISOString(),
    closedAt: null,
  };
}

export const CASHIER_ACTIVE_ORDER_SEED: CashierOrder = buildActiveOrder();

export const CASHIER_PARKED_ORDERS_SEED: readonly CashierOrder[] = [
  buildParkedOrder(
    'luna',
    'Mehmet Yıldız',
    'Luna',
    'Luna (Golden) · Grooming + Pire',
    [
      {
        lineId: 'line_p1_1',
        itemId: 'svc_grooming_full',
        label: 'Full Grooming Paketi',
        qty: 1,
        unitPriceMinor: 45_000,
        lineTotalMinor: 45_000,
        taxRate: 0.2,
        discount: null,
        note: null,
      },
      {
        lineId: 'line_p1_2',
        itemId: 'svc_grooming_flea',
        label: 'Pire/Kene Banyosu',
        qty: 1,
        unitPriceMinor: 12_000,
        lineTotalMinor: 12_000,
        taxRate: 0.2,
        discount: null,
        note: null,
      },
    ],
  ),
  buildParkedOrder(
    'bulut',
    'Zeynep Aksoy',
    'Bulut',
    'Bulut (Scottish) · Pansiyon 2 gece',
    [
      {
        lineId: 'line_p2_1',
        itemId: 'svc_boarding_night',
        label: 'Pansiyon · 1 Gece',
        qty: 2,
        unitPriceMinor: 30_000,
        lineTotalMinor: 60_000,
        taxRate: 0.2,
        discount: null,
        note: null,
      },
    ],
  ),
];

export function computeOrderTotals(
  lines: readonly CashierOrderLine[],
): { subtotalMinor: number; orderDiscountMinor: number; taxMinor: number; totalMinor: number } {
  const subtotal = lines.reduce(
    (acc, l) => acc + l.qty * l.unitPriceMinor,
    0,
  );
  const orderDiscount = lines.reduce(
    (acc, l) => acc + (l.discount?.valueMinor ?? 0),
    0,
  );
  const taxableBase = Math.max(0, subtotal - orderDiscount);
  // Per-line tax rate could differ; approximate with per-line weighted sum.
  const tax = lines.reduce((acc, l) => {
    const lineBase = Math.max(
      0,
      l.qty * l.unitPriceMinor - (l.discount?.valueMinor ?? 0),
    );
    return acc + Math.round(lineBase * l.taxRate);
  }, 0);
  return {
    subtotalMinor: subtotal,
    orderDiscountMinor: orderDiscount,
    taxMinor: tax,
    totalMinor: taxableBase + tax,
  };
}
